import React, { useEffect, useState } from "react";
import { Row, Col, Button, Input, Label, FormFeedback } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  addProductPremium,
  deleteproductpremium,
  updateProductPremium,
} from "services/insuranceService";

const PremiumForm = ({
  validation,
  allowDelete = false,
  isEdit,
  isUpdateProductMode = false,
}: {
  validation: any;
  allowDelete?: boolean;
  isEdit?: boolean;
  isUpdateProductMode?: boolean;
}) => {
  const [savedRowIndices, setSavedRowIndices] = useState<Set<number>>(new Set());
  const productId = validation.values?.id;

  useEffect(() => {
    setSavedRowIndices(new Set());
  }, [productId]);
  const ageBandOptions = [
    { value: "01-03", label: "01-03" },
    { value: "04-06", label: "04-06" },
    { value: "07-09", label: "07-09" },
    { value: "10-11", label: "10-11" },
    { value: "18-30", label: "18-30" },
    { value: "31-45", label: "31-45" },
    { value: "46-55", label: "46-55" },
    { value: "56-60", label: "56-60" },
  ];
  const canDelete = allowDelete || !!isEdit;
  const withStatus = (payload: any) => ({
    ...payload,
    status: validation.values.status,
  });

  const premiumRows = validation.values.premium_table || [];
  const requiredFields = ["age", "monthly_bd", "quarterly_bd", "half_yearly_bd", "yearly_bd", "sum_assured"];

  const isEmpty = (val: any) =>
    val === undefined ||
    val === null ||
    (typeof val === "string" && val.trim() === "");

  const isRowFieldsComplete = (row: any): boolean => {
    if (isEmpty(validation.values.premium_note)) {
      return false;
    }
    return requiredFields.every((f) => !isEmpty(row?.[f]));
  };

  const validateRowFields = (row: any): boolean => {
    // premium note required
    if (isEmpty(validation.values.premium_note)) {
      validation.setFieldTouched("premium_note", true, false);
      toast.error("Premium Note is required.");
      return false;
    }

    const missing = requiredFields.filter((f) => isEmpty(row?.[f]));
    if (missing.length) {
      toast.error("Please fill all premium table fields before saving.");
      return false;
    }
    return true;
  };

  // Ensure at least one row exists for user to fill
  useEffect(() => {
    if (!premiumRows.length) {
      validation.setFieldValue("premium_table", [
        {
          age: "",
          monthly_bd: "",
          quarterly_bd: "",
          half_yearly_bd: "",
          yearly_bd: "",
          sum_assured: "",
          saved: false,
          isEditing: true,
        },
      ]);
    }
  }, [premiumRows.length, validation]);

  // Require current rows to be filled before allowing another
  const canAddMore = premiumRows.every((row: any) => {
    const required = [
      row?.age,
      row?.monthly_bd,
      row?.quarterly_bd,
      row?.half_yearly_bd,
      row?.yearly_bd,
      row?.sum_assured,
    ];
    return required.every((val) => val !== undefined && String(val).trim() !== "");
  });

  // ADD NEW ROW
  const addRow = () => {
    if (!canAddMore) {
      toast.error("Please fill the current premium row before adding more.");
      return;
    }
    validation.setFieldValue("premium_table", [
      {
        age: "",
        monthly_bd: "",
        quarterly_bd: "",
        half_yearly_bd: "",
        yearly_bd: "",
        sum_assured: "",
        saved: false,
        isEditing: true,
      },
      ...premiumRows,
    ]);
  };

  const deleteRow = async (idx: number) => {
    const row = validation.values.premium_table[idx];

    try {
      if (row?.id) {
        const response = await deleteproductpremium(row.id);
          if (response?.success) {
          const updated = [...validation.values.premium_table];
          updated.splice(idx, 1);
          validation.setFieldValue("premium_table", updated);
          toast.success(response?.message || "Row deleted successfully!"); 
        } else {
          toast.error(response?.message || "Failed to delete row!"); 
        }
      } else {
        const updated = [...validation.values.premium_table];
        updated.splice(idx, 1);
        validation.setFieldValue("premium_table", updated);
        toast.success("Row removed successfully!"); 
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to delete row!"); 
    }
  };

    const saveRow = async (idx: number) => {
    const row = validation.values.premium_table[idx];

    if (!validateRowFields(row)) return;

    const payload = {
      product_id: validation.values.id,
      premium_note: validation.values.premium_note,
      age: row.age,
      monthly_bd: Number(row.monthly_bd),
      quarterly_bd: Number(row.quarterly_bd),
      half_yearly_bd: Number(row.half_yearly_bd),
      yearly_bd: Number(row.yearly_bd),
      sum_assured: Number(row.sum_assured),
    };

    try {
      const res = await addProductPremium(payload);
      toast.success(`Premium row ${idx + 1} saved!`);

      const data = [...validation.values.premium_table];
      data[idx] = { ...row, id: res?.data, saved: true, isEditing: false };
      validation.setFieldValue("premium_table", data);
      setSavedRowIndices((prev) => new Set(prev).add(idx));
    } catch (error) {
      console.log(error);
      toast.error("Error saving premium data!");
    }
  };

  // UPDATE EXISTING ROW
  const updateRow = async (idx: number) => {
    const row = validation.values.premium_table[idx];
    if (!row?.id) return;

    if (!validateRowFields(row)) return;

    const payload = {
      premium_id: row.id,
      product_id: validation.values.id,
      premium_note: validation.values.premium_note,
      age: row.age,
      monthly_bd: Number(row.monthly_bd),
      quarterly_bd: Number(row.quarterly_bd),
      half_yearly_bd: Number(row.half_yearly_bd),
      yearly_bd: Number(row.yearly_bd),
      sum_assured: Number(row.sum_assured),
    };
 
    try {
      await updateProductPremium(row.id, withStatus(payload));
      const data = [...validation.values.premium_table];
      data[idx] = { ...row, saved: true, isEditing: false };
      validation.setFieldValue("premium_table", data);
      validation.setFieldValue("id", validation.values.id, false);
      validation.setFieldValue("product_id", validation.values.product_id, false);

      toast.success("Premium row updated!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update row!");
    }
  };

  return (
    <div>
      {canAddMore && (
        <div className="d-flex justify-content-end mt-3">
          <Button color="dark" outline onClick={addRow}>
            + Add Row
          </Button>
        </div>
      )}

      <Label className="fw-bold mb-1 mt-2">Premium Table Note</Label>
      <Input
        type="textarea"
        name="premium_note"
        placeholder="Type here.."
        value={validation.values.premium_note}
        onChange={validation.handleChange}
        invalid={!!(validation.touched.premium_note && validation.errors.premium_note)}
        rows={3}
      />
      <FormFeedback>{validation.errors.premium_note}</FormFeedback>

      {validation.values.premium_table?.map((row: any, idx: number) => (
        <div key={idx} className="border rounded p-3 mt-4 bg-light position-relative">

          {/* Delete only when allowed (edit mode) */}
          {canDelete && (
            <i
              className="ri-delete-bin-line text-danger fs-5 position-absolute"
              style={{ top: "10px", right: "10px", cursor: "pointer" }}
              onClick={() => deleteRow(idx)}
            ></i>
          )}

          <Label className="fw-bold mb-2">Premium Table (Row {idx + 1})</Label>

          <Row>
            <Col md={4}>
            <Select
                options={ageBandOptions}
                name="age"
                placeholder="Age"
                value={
                  row.age
                    ? ageBandOptions.find((opt) => opt.value === row.age) || null
                    : null
                }
                onChange={(e: any) =>
                  validation.setFieldValue(`premium_table[${idx}].age`, e?.value)
                }
                isDisabled={row.saved && row.isEditing}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={12} className="d-flex flex-wrap gap-3">
              {["monthly_bd", "quarterly_bd", "half_yearly_bd", "yearly_bd", "sum_assured"].map((field) => (
                <Input
                  key={field}
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  name={`premium_table[${idx}].${field}`}
                  placeholder={field.replace("_", " ").toUpperCase()}
                  value={row[field] || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 8);
                    validation.setFieldValue(`premium_table[${idx}].${field}`, v);
                  }}
                  disabled={row.saved && row.isEditing}
                  style={{ width: "150px" }}
                />
              ))}
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3">
            {!row.saved ? (
              savedRowIndices.has(idx) ? (
                <Button color="primary" disabled>
                  Saved
                </Button>
              ) : (
                <Button
                  color="primary"
                  onClick={() => saveRow(idx)}
                  disabled={!isRowFieldsComplete(row)}
                >
                  Save Premium
                </Button>
              )
            ) : isUpdateProductMode ? (
              <Button
                color="primary"
                onClick={() => updateRow(idx)}
                disabled={!isRowFieldsComplete(row)}
              >
                Update Premium
              </Button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PremiumForm;
